import { Paginator } from "@/components/paginator";
import { SelectNumberOfRows } from "./-components/select-number-of-rows";
import { CurrencyIcons } from "@/components/svg/currencyIcons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn, numberToDecimalWithCurrency } from "@/lib/utils";
import { VolumeApi } from "@/lib/volume-api";
import { Payouts } from "@/types/payments";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { z } from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import { AuthorityRequired } from "@/lib/auth";
import { SelectSingleCurrency } from "./-components/payout/select-single-currency";
import { Spinner } from "@/components/ui/spinner";
import { VolumeDatePicker } from "@/components/date-range";
import { useSelectedApplication } from "@/hooks/use-selected-application";
import { CardWithHeader } from "@/components/card-with-header";
import { GenerateReport } from "./-components/generate-report";

const defaultFromDate = dayjs()
  .subtract(30, "day")
  .startOf("day")
  .toISOString();
const defaultToDate = dayjs().endOf("day").toISOString();
const defaultCurrency = "GBP";
export const defaultRows = 10;
const defaultPage = 1;

const searchParams = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().optional(),
  currency: z.string().optional(),
  rows: z.number().optional(),
});

export const Route = createFileRoute(
  "/(app)/_main-layout/_payments/payments/payout",
)({
  component: () => <PayoutsComponent />,
  validateSearch: zodValidator(searchParams),
  staleTime: Infinity,
  loaderDeps: ({
    search: { from, page, to, currency, rows, applicationId },
  }) => ({
    from,
    page,
    to,
    currency,
    rows,
    applicationId,
  }),
  loader: async ({
    context,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deps: { from, page, to, currency, rows, applicationId },
  }) => {
    const api = new VolumeApi(
      context.accessToken,
      context.selectedApplicationId,
    );
    return api.getPayouts({
      currency: currency ?? defaultCurrency,
      endDateTimeUtc: to ?? defaultToDate,
      merchantId: context.merchantId,
      page: ((page ?? defaultPage) - 1).toString(),
      size: (rows ?? defaultRows).toString(),
      startDateTimeUtc: from ?? defaultFromDate,
      sort: "createdAt,desc",
    });
  },
  pendingComponent: () => (
    <div className="ml-4">
      Please wait. Fetching Payouts may take up to 10 seconds.
      <Spinner width={40} height={40} />
    </div>
  ),
});

const PayoutsComponent = () => {
  const context = Route.useRouteContext();
  const search = Route.useSearch();
  const payouts = Route.useLoaderData();
  const navigate = useNavigate();
  const selectedApplication = useSelectedApplication();

  const startDateTimeUtc = search.from ?? defaultFromDate;
  const endDateTimeUtc = search.to ?? defaultToDate;

  if (!selectedApplication.hasVirtualAccountsEnabled) {
    return <Navigate to="/payments/payin" />;
  }

  const hasGBP = selectedApplication.payeeDetailsPerCurrency?.some(
    (x) => x.forCurrency === "GBP",
  );
  const hasEURForDefaultVirtual =
    selectedApplication.payeeDetailsPerCurrency?.some(
      (x) =>
        x.forCurrency === "EUR" && x.defaultForCurrency && x.isVirtualAccount,
    );
  const hasGBPForDefaultVirtual =
    selectedApplication.payeeDetailsPerCurrency?.some(
      (x) =>
        x.forCurrency === "GBP" && x.defaultForCurrency && x.isVirtualAccount,
    );

  return (
    <CardWithHeader title="Pay Outs">
      <div className="inline-flex w-full items-center justify-between gap-4">
        <div className="w-full flex items-center justify-start gap-4">
          {hasEURForDefaultVirtual && hasGBPForDefaultVirtual ? (
            <SelectSingleCurrency />
          ) : (
            <div
              className="items-top flex space-x-2 px-4 pl-4 my-[4.5px]  w-48 border border-neutral-200 h-10 py-2 rounded-lg"
              data-testid="single-currency"
            >
              <div className="grid gap-1.5 leading-none mr-auto">
                <p className="flex gap-2 items-center leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {hasGBP ? CurrencyIcons.GBP : CurrencyIcons.EUR}
                  {hasGBP ? "GBP" : "EUR"}
                </p>
              </div>
            </div>
          )}
          <VolumeDatePicker
            onChange={(range) => {
              navigate({
                to: location.pathname,
                search: (currentSearch) => ({
                  ...currentSearch,
                  from: range.from.toISOString(),
                  to: range.to.toISOString(),
                  page: 1,
                }),
              });
            }}
            initialDate={{
              from: new Date(
                search.from || dayjs().subtract(30, "day").toDate(),
              ),
              to: new Date(search.to || dayjs().toDate()),
            }}
          />
          <div className="ml-auto flex items-center gap-2">
            <AuthorityRequired
              authoritiesRequired={["GENERATE_REPORTS"]}
              authAuthorities={context.authorities}
            >
              <GenerateReport
                startDateTimeUtc={startDateTimeUtc}
                endDateTimeUtc={endDateTimeUtc}
                reportType={"payout"}
                merchantId={context.merchantId}
                currency={search.currency}
              />
            </AuthorityRequired>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payout ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Payout Initiated</TableHead>
              <TableHead>Amount</TableHead>
              {atLeastOneRecipientAccSortCodeColumn(payouts) ? (
                <>
                  <TableHead>Recipient Account Number</TableHead>
                  <TableHead>Recipient Sort Code</TableHead>
                </>
              ) : null}
              {atLeastOneRecipientIBANColumn(payouts) ? (
                <TableHead>Recipient IBAN</TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {payouts.content.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  No Pay Outs found
                </TableCell>
              </TableRow>
            ) : null}
            {payouts.content.map((payment) => {
              if (!selectedApplication.hasVirtualAccountsEnabled) return null;
              return (
                <TableRow key={payment.externalProviderPaymentId}>
                  <TableCell>{payment.externalProviderPaymentId}</TableCell>
                  <TableCell>
                    <div
                      className={cn(
                        "text-center rounded-lg px-2 py-[2px] inline-block",
                        payment.status === "PROCESSED" &&
                          "bg-green-200 text-green-600",
                        payment.status === "FAILED" &&
                          "bg-red-200 text-red-600",
                        payment.status === "CANCELLED" &&
                          "bg-purple-200 text-purple-600",
                        payment.status === "HELD" &&
                          "bg-yellow-200 text-yellow-600",
                        payment.status === "RETURNED" &&
                          "bg-orange-200 text-orange-600",
                        payment.status === "IN_PROGRESS" &&
                          "bg-blue-200 text-blue-600",
                      )}
                    >
                      {payment.status === "IN_PROGRESS"
                        ? "In Progress"
                        : payment.status.substring(0, 1) +
                          payment.status.substring(1).toLowerCase()}
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(payment.createdAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                    })}
                  </TableCell>
                  <TableCell className="font-medium">
                    {numberToDecimalWithCurrency(
                      payment.amount,
                      payment.currency,
                    )}
                  </TableCell>
                  {payment.beneficiary.accountNumber ? (
                    <TableCell>{payment.beneficiary.accountNumber}</TableCell>
                  ) : null}
                  {payment.beneficiary.sortCode ? (
                    <TableCell>{payment.beneficiary.sortCode}</TableCell>
                  ) : null}
                  {payment.beneficiary.iban ? (
                    <TableCell>{payment.beneficiary.iban}</TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-center items-center mt-6">
        <div>
          <div className="mr-auto">
            Number of payouts: {payouts.totalElements}
          </div>
        </div>
        <div className="m-auto">
          <Paginator from={Route.id} totalPages={payouts.totalPages} />
        </div>
        <SelectNumberOfRows type={"payout"} />
      </div>
    </CardWithHeader>
  );
};

function atLeastOneRecipientIBANColumn(payouts: Payouts) {
  return payouts.content.some((x) => x.beneficiary.iban !== null);
}
function atLeastOneRecipientAccSortCodeColumn(payouts: Payouts) {
  return (
    payouts.content.some((x) => x.beneficiary.accountNumber !== null) ||
    payouts.content.some((x) => x.beneficiary.sortCode !== null)
  );
}
